import React from 'react';

import {
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  minLength,
  ReferenceArrayInput,
  required,
  SelectInput,
  SimpleFormIterator,
} from 'react-admin';

import { Box } from '@mui/material';

import { useWatch } from 'react-hook-form';

import SmartDateInput from '../../../../customFields/SmartDateInput';
import dateRangeValidation from '../../../utils/reports/dateRangeValidation';
import isPastDate from '../../../utils/reports/isPastDate';
import ReferenceEntitiesInput from '../../../../customFields/ReferenceEntitiesInput';
import entityTypes from '../../../../constants/entityTypes';
import currencies from '../../../../constants/currencies';
import FieldBox from '../FieldBox';
import RangeFields from '../RangeFields';

const BusinessUsageReport = () => {
  const includeAllGroups = useWatch({ name: 'report.includeAllGroups' });
  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <FieldBox title="Businesses *" description="Select the business entities to analyse for usage patterns. Required — at least one business must be selected.">
        <ReferenceEntitiesInput allowedTypes={[entityTypes.BUSINESS]} validate={[required(), minLength(1)]} source="report.businessIds" label="" helperText={false} />
      </FieldBox>
      <FieldBox title="Date range" description="Only usage within this date range is included (CET timezone). 'Date from' defaults to 2000-01-01; 'date to' defaults to generation date.">
        <RangeFields
          from={(
            <SmartDateInput fullWidth validate={[isPastDate, dateRangeValidation]} source="report.from" label="From" helperText={false} />
          )}
          to={(
            <SmartDateInput fullWidth validate={[dateRangeValidation]} source="report.to" label="To" helperText={false} />
          )}
        />
      </FieldBox>
      <FieldBox title="Include all groups" description="If enabled, all groups are included in the report. If disabled, you can select specific groups below." inline>
        <BooleanInput source="report.includeAllGroups" label="" helperText={false} defaultValue={false} />
      </FieldBox>
      {!includeAllGroups && (
        <FieldBox title="Groups" description="IDs of groups to include in the report. Leave empty to include all groups, or use the toggle above to explicitly include all.">
          <ReferenceArrayInput perPage={100} source="report.groupIds" reference="groups" filter={{ active: true }}>
            <AutocompleteArrayInput
              fullWidth
              label=""
              filterToQuery={(searchText: string) => ({ name: searchText })}
              optionText="name"
              helperText={false}
            />
          </ReferenceArrayInput>
        </FieldBox>
      )}
      <FieldBox title="Currency" description="Only transactions in this currency are used for per-business transaction analytics. Default: EUR.">
        <SelectInput
          choices={currencies.map((currency) => ({
            name: currency,
            id: currency,
          }))}
          fullWidth
          label=""
          source="report.currency"
          helperText={false}
        />
      </FieldBox>
      <FieldBox title="Report dates" description="For each date added, the number of forms per individual is counted at end of that date. Leave empty to omit historical form counts.">
        <ArrayInput source="report.reportDates" label="" helperText={false}>
          <SimpleFormIterator
            inline
            disableReordering
            fullWidth
            sx={{
              '& .RaSimpleFormIterator-line': {
                borderBottom: 'none',
                '& .RaSimpleFormIterator-inline': {
                  rowGap: '1em',
                },
              },
            }}
          >
            <SmartDateInput fullWidth source="" />
          </SimpleFormIterator>
        </ArrayInput>
      </FieldBox>
    </Box>
  );
};

export default BusinessUsageReport;
